import { useState, useMemo, useCallback } from "react";
import {
  Hash,
  Users,
  BellOff,
  Bell,
  Archive,
  ArchiveRestore,
  LogOut,
} from "lucide-react";
import { useTeamPresence } from "../../../contexts/TeamPresenceContext";
import {
  archiveConversation,
  muteConversation,
  leaveGroup,
} from "../../../../api/teamChat";

const MAX_VISIBLE_AVATARS = 5;

const TeamChatHeader = ({
  conversation,
  members = {},
  currentTeamId,
  onLeave,
}) => {
  const { onlineUsers, mutedConversations, updateMutedConversation } =
    useTeamPresence();

  const [archivedLocal, setArchivedLocal] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const [archivedLoaded, setArchivedLoaded] = useState(false);
  if (!archivedLoaded && currentTeamId) {
    setArchivedLoaded(true);
  }

  useState(() => {
    const handler = (e) => {
      if (e.detail?.team_id === currentTeamId) {
        setArchivedLocal(e.detail.archived);
      }
    };
    window.addEventListener("team:archive-changed", handler);
    return () => window.removeEventListener("team:archive-changed", handler);
  });

  const isMuted = mutedConversations.includes(currentTeamId);
  const convType = conversation?.type || "group";
  const convName =
    conversation?.name || conversation?.dm_display_name || "Conversation";

  const memberList = useMemo(() => {
    const list = Object.values(members);
    return list.sort((a, b) => {
      const aOnline = onlineUsers.has(a.uuid) ? 1 : 0;
      const bOnline = onlineUsers.has(b.uuid) ? 1 : 0;
      return bOnline - aOnline;
    });
  }, [members, onlineUsers]);

  const onlineCount = useMemo(
    () => memberList.filter((m) => onlineUsers.has(m.uuid)).length,
    [memberList, onlineUsers],
  );

  const visibleMembers = memberList.slice(0, MAX_VISIBLE_AVATARS);
  const overflowCount = memberList.length - MAX_VISIBLE_AVATARS;

  const handleMute = useCallback(async () => {
    await muteConversation(currentTeamId, !isMuted);
    updateMutedConversation(currentTeamId, !isMuted);
  }, [currentTeamId, isMuted, updateMutedConversation]);

  const handleArchive = useCallback(async () => {
    const newState = !archivedLocal;
    await archiveConversation(currentTeamId, newState);
    setArchivedLocal(newState);
    window.dispatchEvent(new CustomEvent("sidebar:refresh-team-conversations"));
  }, [currentTeamId, archivedLocal]);

  const handleLeave = useCallback(async () => {
    if (leaving) return;
    setLeaving(true);
    try {
      await leaveGroup(currentTeamId);
      window.dispatchEvent(
        new CustomEvent("sidebar:refresh-team-conversations"),
      );
      onLeave?.();
    } catch (e) {
      console.error("Failed to leave:", e);
    } finally {
      setLeaving(false);
    }
  }, [currentTeamId, leaving, onLeave]);

  if (!conversation) return null;

  const TypeIcon =
    convType === "channel" ? Hash : convType === "dm" ? null : Users;

  const actionBtnBase =
    "p-1.5 rounded-lg transition cursor-pointer outline-none ring-inputFocus/60 focus-visible:ring-2";
  const actionBtnInactive =
    "text-mutedText hover:text-mainText hover:bg-mainButtonBg/50";

  return (
    <div className="flex items-center gap-x-3 px-4 py-2 border-b border-surfaceBorder bg-surface/80 backdrop-blur-md shrink-0 mx-4 my-2 rounded-lg">
      <div className="flex items-center gap-x-2 min-w-0 flex-1">
        {convType === "dm" ? (
          (() => {
            const dmName = conversation.dm_display_name || "Unknown";
            const initial = (dmName[0] || "?").toUpperCase();
            const dmUuid = conversation.dm_user_uuid;
            const isOnline = onlineUsers.has(dmUuid);
            return (
              <span className="relative shrink-0">
                <span className="w-7 h-7 rounded-full bg-velBlue/20 flex items-center justify-center text-xs font-normal text-velBlue">
                  {initial}
                </span>
                {isOnline && (
                  <span
                    className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-successDot rounded-full border-2 border-mainBg"
                    style={{ boxShadow: "0 0 6px rgba(34,197,94,0.4)" }}
                  />
                )}
              </span>
            );
          })()
        ) : (
          <div
            className={[
              "w-7 h-7 rounded-lg flex items-center justify-center shrink-0",
              convType === "channel" ? "bg-activeCase/10" : "bg-velBlue/10",
            ].join(" ")}
          >
            <TypeIcon
              className={[
                "h-4 w-4",
                convType === "channel" ? "text-activeCase" : "text-velBlue",
              ].join(" ")}
              strokeWidth={2}
            />
          </div>
        )}

        <div className="min-w-0">
          <h2 className="text-sm font-normal text-mainText truncate leading-tight">
            {convName}
          </h2>
          <p className="text-xs font-normal text-mutedText leading-tight">
            {memberList.length} member{memberList.length !== 1 ? "s" : ""}
            {onlineCount > 0 && (
              <span className="text-activeCase ml-1">
                · {onlineCount} online
              </span>
            )}
          </p>
        </div>
      </div>

      <div className="flex items-center shrink-0">
        <div className="flex -space-x-1.5">
          {visibleMembers.map((member) => {
            const name = member.display_name || member.first_name || "?";
            const initial = (name[0] || "?").toUpperCase();
            const isOnline = onlineUsers.has(member.uuid);
            return (
              <div
                key={member.uuid}
                className="relative"
                title={`${name}${isOnline ? " (online)" : ""}`}
              >
                <div
                  className={[
                    "w-6 h-6 rounded-full flex items-center justify-center text-[0.625rem] font-normal text-white border-2 border-mainBg",
                    isOnline ? "bg-velBlue" : "bg-mutedText/40",
                  ].join(" ")}
                >
                  {initial}
                </div>
              </div>
            );
          })}
          {overflowCount > 0 && (
            <div className="w-6 h-6 rounded-full flex items-center justify-center text-[0.5625rem] font-normal text-mutedText bg-surface/80 border-2 border-mainBg">
              +{overflowCount}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-x-1 shrink-0">
        <button
          onClick={handleMute}
          title={isMuted ? "Unmute" : "Mute"}
          className={[
            actionBtnBase,
            isMuted
              ? "text-sunColor bg-sunColor/10 hover:bg-sunColor/20"
              : actionBtnInactive,
          ].join(" ")}
        >
          {isMuted ? (
            <BellOff className="h-4 w-4" strokeWidth={2} />
          ) : (
            <Bell className="h-4 w-4" strokeWidth={2} />
          )}
        </button>

        <button
          onClick={handleArchive}
          title={archivedLocal ? "Unarchive" : "Archive"}
          className={[
            actionBtnBase,
            archivedLocal
              ? "text-velBlue bg-velBlue/10 hover:bg-velBlue/20"
              : actionBtnInactive,
          ].join(" ")}
        >
          {archivedLocal ? (
            <ArchiveRestore className="h-4 w-4" strokeWidth={2} />
          ) : (
            <Archive className="h-4 w-4" strokeWidth={2} />
          )}
        </button>

        {convType === "group" && (
          <button
            onClick={handleLeave}
            disabled={leaving}
            title="Leave group"
            className={[
              actionBtnBase,
              "text-mutedText hover:text-logoutColor hover:bg-logoutColor/10 disabled:opacity-50",
            ].join(" ")}
          >
            <LogOut className="h-4 w-4" strokeWidth={2} />
          </button>
        )}
      </div>
    </div>
  );
};

export default TeamChatHeader;
